import { callRulesSchema, maxGreetingBytes, type CallRules, type CallRulesView, type UnansweredTarget } from '@smarton-pbx/shared';
import { Mic, PhoneForwarded, Square, Trash2, Upload } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Field, Loading, Notice, Pill } from '../components/ui';
import { language, locale, t } from '../i18n';
import { formatNumber } from '../lib/calls';
import { useSubmit } from '../lib/hooks';
import { useRecorder } from './flows/recorder';

const languageNames = new Intl.DisplayNames([language], { type: 'language' });

const day = (value: string) => new Date(`${value}T12:00:00`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * "When I can't answer": where calls for a person's own number go when they
 * don't answer, are on a call or are away, and their voicemail greetings.
 * `userId` null: the signed-in person (Account); else an admin editing
 * someone (People).
 */
export function CallRulesCard({ userId = null, name }: { userId?: string | null; name?: string }) {
  const [view, setView] = useState<CallRulesView | null>(null);
  const [error, setError] = useState<string>();

  useEffect(() => {
    api.callRules(userId).then(setView, (err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, [userId]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!view) return <Loading />;
  return (
    <>
      <RulesForm view={view} userId={userId} name={name} onSaved={setView} />
      <Greetings view={view} userId={userId} onChanged={setView} />
    </>
  );
}

function RulesForm({ view, userId, name, onSaved }: { view: CallRulesView; userId: string | null; name?: string | undefined; onSaved: (view: CallRulesView) => void }) {
  const [rules, setRules] = useState<CallRules>(view.rules);
  const [saved, setSaved] = useState(false);
  const form = useSubmit();
  const self = userId === null;

  const change = (patch: Partial<CallRules>) => {
    setSaved(false);
    setRules((current) => ({ ...current, ...patch }));
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const result = await form.submit(callRulesSchema, rules, (input) => api.updateCallRules(input, userId));
    if (result) {
      onSaved(result);
      setRules(result.rules);
      setSaved(true);
    }
  }

  const colleague = view.colleagues.find((c) => c.id === rules.unansweredUserId)?.name;
  const queue = view.queues.find((q) => q.id === rules.unansweredQueueId)?.name;
  const then =
    rules.unanswered === 'COLLEAGUE' && colleague
      ? self
        ? t('If nobody answers, {colleague}’s browser and app ring; if they don’t answer either, the caller can leave you a message.', { colleague })
        : t('If nobody answers, {colleague}’s browser and app ring; if they don’t answer either, the caller can leave them a message.', { colleague })
      : rules.unanswered === 'QUEUE' && queue
        ? t('If nobody answers, the call goes to the {queue} queue, and its own settings take it from there.', { queue })
        : self
          ? t('If nobody answers, the caller can leave you a message.')
          : t('If nobody answers, the caller can leave them a message.');
  const mobile = rules.ringMobile && view.mobileNumber;
  const numbers = new Intl.ListFormat(language, { type: 'disjunction' }).format(view.numbers.map(formatNumber));
  const skip =
    rules.awayUntil && rules.forwardWhenBusy
      ? self
        ? t('While you’re away, and while you’re on a call, calls skip you and go straight to the last step.')
        : t('While they’re away, and while they’re on a call, calls skip them and go straight to the last step.')
      : rules.awayUntil
        ? self
          ? t('While you’re away, calls skip you and go straight to the last step.')
          : t('While they’re away, calls skip them and go straight to the last step.')
        : rules.forwardWhenBusy
          ? self
            ? t('While you’re on a call, calls skip you and go straight to the last step.')
            : t('While they’re on a call, calls skip them and go straight to the last step.')
          : null;

  return (
    <form className="card" onSubmit={submit} noValidate data-testid="call-rules">
      <div className="card-header">
        <div className="row" style={{ gap: '0.6rem' }}>
          <PhoneForwarded size={18} className="subtle" aria-hidden />
          <h2>{self ? t('When I can’t answer') : name ? t('When {name} can’t answer', { name }) : t('When they can’t answer')}</h2>
        </div>
        {view.awayNow && (
          <Pill kind="warning" dot>
            {t('Away until {date}', { date: day(view.rules.awayUntil!) })}
          </Pill>
        )}
      </div>
      <div className="card-body stack">
        {view.numbers.length === 0 ? (
          <Notice>
            {self
              ? t('You don’t have a number of your own yet. These settings apply once an admin gives you one on the Numbers page.')
              : name
                ? t('{name} doesn’t have a number of their own yet. These settings apply once an admin gives them one on the Numbers page.', { name })
                : t('They don’t have a number of their own yet. These settings apply once an admin gives them one on the Numbers page.')}
          </Notice>
        ) : (
          <ol className="steps" data-testid="call-rules-steps">
            <li>
              {self
                ? rules.forwardWhenBusy
                  ? t('A call to {numbers} rings your browser and app (unless you’re on a call).', { numbers })
                  : t('A call to {numbers} rings your browser and app.', { numbers })
                : rules.forwardWhenBusy
                  ? t('A call to {numbers} rings their browser and app (unless they’re on a call).', { numbers })
                  : t('A call to {numbers} rings their browser and app.', { numbers })}
            </li>
            {mobile && (
              <li>
                {self
                  ? t('If you don’t answer, it rings your mobile {number}.', { number: formatNumber(view.mobileNumber!) })
                  : t('If they don’t answer, it rings their mobile {number}.', { number: formatNumber(view.mobileNumber!) })}
              </li>
            )}
            <li>{then}</li>
            {skip && <li>{skip}</li>}
          </ol>
        )}
        <div className="form-grid">
          <Field label={t('If nobody answers')} error={form.fields.unanswered}>
            <select value={rules.unanswered} onChange={(e) => change({ unanswered: e.target.value as UnansweredTarget })} data-testid="unanswered">
              <option value="VOICEMAIL">{self ? t('My voicemail') : t('Their voicemail')}</option>
              <option value="COLLEAGUE" disabled={view.colleagues.length === 0}>
                {t('A colleague')}
              </option>
              <option value="QUEUE" disabled={view.queues.length === 0}>
                {t('A queue')}
              </option>
            </select>
          </Field>
          {rules.unanswered === 'COLLEAGUE' && (
            <Field label={t('Colleague')} error={form.fields.unansweredUserId}>
              <select value={rules.unansweredUserId ?? ''} onChange={(e) => change({ unansweredUserId: e.target.value || null })} data-testid="unanswered-colleague">
                <option value="">{t('Choose…')}</option>
                {view.colleagues.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {rules.unanswered === 'QUEUE' && (
            <Field label={t('Queue')} error={form.fields.unansweredQueueId}>
              <select value={rules.unansweredQueueId ?? ''} onChange={(e) => change({ unansweredQueueId: e.target.value || null })} data-testid="unanswered-queue">
                <option value="">{t('Choose…')}</option>
                {view.queues.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label={self ? t('When I’m on a call') : t('When they’re on a call')} error={form.fields.forwardWhenBusy}>
            <select value={rules.forwardWhenBusy ? 'pass' : 'ring'} onChange={(e) => change({ forwardWhenBusy: e.target.value === 'pass' })} data-testid="when-busy">
              <option value="ring">{self ? t('Ring me anyway') : t('Ring them anyway')}</option>
              <option value="pass">{self ? t('Skip me') : t('Skip them')}</option>
            </select>
          </Field>
          <Field
            label={t('Mobile')}
            hint={view.mobileNumber ? undefined : t('No mobile number. An admin adds it on the People page.')}
            error={form.fields.ringMobile}
          >
            <select
              value={rules.ringMobile && view.mobileNumber ? 'ring' : 'skip'}
              disabled={!view.mobileNumber}
              onChange={(e) => change({ ringMobile: e.target.value === 'ring' })}
              data-testid="ring-mobile"
            >
              <option value="ring">{self ? t('Rings after your browser and app') : t('Rings after their browser and app')}</option>
              <option value="skip">{t('Doesn’t ring')}</option>
            </select>
          </Field>
        </div>

        <div className="stack" style={{ gap: '0.5rem' }}>
          <div style={{ fontWeight: 560 }}>{t('Away')}</div>
          <div className="form-grid">
            <Field label={t('From')} hint={t('Empty: from now')} error={form.fields.awayFrom}>
              <input type="date" value={rules.awayFrom ?? ''} onChange={(e) => change({ awayFrom: e.target.value || null })} data-testid="away-from" />
            </Field>
            <Field label={t('Last day away')} error={form.fields.awayUntil}>
              <input type="date" value={rules.awayUntil ?? ''} min={rules.awayFrom ?? undefined} onChange={(e) => change({ awayUntil: e.target.value || null })} data-testid="away-until" />
            </Field>
          </div>
          <p className="subtle" style={{ fontSize: '0.85rem' }}>
            {self
              ? t('While away, your browser, app and mobile don’t ring, your queues skip you, and colleagues calling you hear that you can’t answer. It ends by itself after the last day.')
              : t('While away, their browser, app and mobile don’t ring, their queues skip them, and colleagues calling them hear that they can’t answer. It ends by itself after the last day.')}
          </p>
        </div>

        {form.error && <Notice kind="error">{form.error}</Notice>}
        <div className="row" style={{ gap: '0.75rem' }}>
          <button type="submit" disabled={form.busy} data-testid="save-call-rules">
            {t('Save')}
          </button>
          {rules.awayUntil && (
            <button type="button" className="secondary" onClick={() => change({ awayFrom: null, awayUntil: null })} data-testid="back-now">
              {view.awayNow ? (self ? t('I’m back') : t('They’re back')) : t('Not away')}
            </button>
          )}
          {saved && <span className="subtle">{t('Saved.')}</span>}
        </div>
      </div>
    </form>
  );
}

/** Their own voicemail greetings, one per language callers may hear; otherwise the standard one. */
function Greetings({ view, userId, onChanged }: { view: CallRulesView; userId: string | null; onChanged: (view: CallRulesView) => void }) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string | null>(null);
  const [recordingFor, setRecordingFor] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadFor = useRef('');
  const self = userId === null;

  async function act(language: string, work: () => Promise<CallRulesView>) {
    setError(undefined);
    setBusy(language);
    try {
      onChanged(await work());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const recorder = useRecorder(
    (audio) => {
      const language = recordingFor!;
      setRecordingFor(null);
      void act(language, () => api.uploadGreeting(language, audio, userId));
    },
    (message) => {
      setRecordingFor(null);
      setError(message);
    },
  );

  // While an upload is being converted, check back.
  useEffect(() => {
    if (!view.greetings.some((g) => g.status === 'PENDING')) return;
    const timer = setTimeout(() => void api.callRules(userId).then(onChanged), 1500);
    return () => clearTimeout(timer);
  }, [view, userId, onChanged]);

  const languages = [...new Set([...view.languages, ...view.greetings.map((g) => g.language)])];
  return (
    <div className="card" data-testid="greetings">
      <div className="card-header">
        <div className="row" style={{ gap: '0.6rem' }}>
          <Mic size={18} className="subtle" aria-hidden />
          <h2>{t('Voicemail greeting')}</h2>
        </div>
      </div>
      <div className="card-body stack">
        <p className="muted">
          {self
            ? t('Callers who leave you a message hear your own greeting in their language, e.g. “You’ve reached Jonas from sales. Leave a message after the tone and I’ll call you back.” Without one, they hear the standard greeting.')
            : t('Callers who leave them a message hear their own greeting in their language, e.g. “You’ve reached Jonas from sales. Leave a message after the tone and I’ll call you back.” Without one, they hear the standard greeting.')}
        </p>
        <input
          ref={fileInput}
          type="file"
          accept="audio/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            if (file.size > maxGreetingBytes) return setError(t('Use a file under 5 MB.'));
            void act(uploadFor.current, () => api.uploadGreeting(uploadFor.current, file, userId));
          }}
          data-testid="greeting-file"
        />
        {languages.map((language) => {
          const greeting = view.greetings.find((g) => g.language === language);
          const isRecording = recorder.recording && recordingFor === language;
          return (
            <div key={language} className="announcement-row" data-testid="greeting" data-language={language} data-status={greeting?.status ?? 'NONE'}>
              <span className="announcement-language">{languageNames.of(language)}</span>
              {greeting?.status === 'READY' ? (
                <audio
                  controls
                  preload="none"
                  src={api.greetingAudioUrl(language, userId)}
                  aria-label={t('{language} greeting', { language: languageNames.of(language) ?? language })}
                />
              ) : (
                <span className="subtle grow">
                  {greeting?.status === 'PENDING' ? t('Converting…') : greeting?.status === 'FAILED' ? t('That file couldn’t be used.') : t('Standard greeting')}
                </span>
              )}
              {isRecording ? (
                <button type="button" className="small danger" onClick={recorder.stop}>
                  <Square size={14} aria-hidden /> {t('Stop · {n} s', { n: recorder.seconds })}
                </button>
              ) : (
                <button
                  type="button"
                  className="secondary small"
                  disabled={recorder.recording || busy !== null}
                  onClick={() => {
                    setRecordingFor(language);
                    void recorder.start();
                  }}
                >
                  <Mic size={14} aria-hidden /> {t('Record')}
                </button>
              )}
              <button
                type="button"
                className="secondary small"
                disabled={busy === language || recorder.recording}
                onClick={() => {
                  uploadFor.current = language;
                  fileInput.current?.click();
                }}
              >
                <Upload size={14} aria-hidden /> {greeting ? t('Replace') : t('Upload')}
              </button>
              {greeting && (
                <button
                  type="button"
                  className="ghost icon"
                  aria-label={t('Remove the {language} greeting', { language: languageNames.of(language) ?? language })}
                  onClick={() => void act(language, () => api.deleteGreeting(language, userId))}
                >
                  <Trash2 size={15} aria-hidden />
                </button>
              )}
            </div>
          );
        })}
        {error && <Notice kind="error">{error}</Notice>}
      </div>
    </div>
  );
}
